import React, {useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Stack,
    Link,
    Text,
    useBreakpointValue,
    Image,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'

import Banner from './partials/banner'
import HeroButton from '../hero/partials/hero-button'
import PDComponent from '../../pd-component'
import Video from '../hero/partials/video'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

export const StoryTelling = ({
    regions,
    desktopVideo,
    desktopVideoThumbnail,
    mobileVideo,
    mobileVideoThumbnail,
    videoAlt,
    videoEnableSound,
    videoDisableAutoPlay,
    desktopImage,
    mobileImage,
    imageAlt,
    desktopImagePosition = 'right',
    mobileImagePosition = 'bottom',
    imageBackgroundColor = 'white',
    imageBackground = '',

    title,
    titleTag = 'h2',
    titleColor = 'black',
    headlineAlignment,
    titleFontFamily,

    subCopy,
    subCopyFontFamily = 'heading',
    subCopyColor = 'black',
    subCopyTag = 'p',

    bodyCopy,
    bodyCopyColor = 'black',
    bodyFontFamily,

    buttonText1,
    buttonColor1 = 'black',
    buttonBgColor1,
    buttonLink1,
    buttonLinkAriaLabel1,
    dropdownLinkLabel1,
    dropdownLinkUrl1,
    dropdownLinkLabel2,
    dropdownLinkUrl2,
    dropdownLinkLabel3,
    dropdownLinkUrl3,
    dropdownLinkLabel4,
    dropdownLinkUrl4,
    dropdownLinkLabel5,
    dropdownLinkUrl5,
    dropdownLinkLabel6,
    dropdownLinkUrl6,

    ctaTypeMobile = 'button',
    ctaTypeDesktop = 'button',
    ctaSizeMobile = 'normal',
    ctaSizeDesktop = 'normal',

    overlayLink,
    overlayLinkAriaLabel,

    imageLogo1 = '',
    imageLogo2 = '',
    imageLogo1Alt = '',
    imageLogo2Alt = '',

    typeId,
    name
}) => {
    const shouldDisplayVideo = desktopVideo || mobileVideo
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const hasButton1 = buttonText1 && buttonLink1
    const bannerComponentOrder = useBreakpointValue({
        base: mobileImagePosition === 'top' ? '1' : '2',
        md: desktopImagePosition === 'left' ? '1' : '2'
    })
    const headLineComponentOrder = useBreakpointValue({
        base: mobileImagePosition === 'top' ? '2' : '1',
        md: desktopImagePosition === 'left' ? '2' : '1'
    })

    const calloutsComponents =
        regions?.filter(
            (region) => region?.id === 'customFlagsCallouts' && region?.components?.length > 0
        )?.[0]?.components || []

    const imageStyles = imageBackground
        ? {backgroundImage: `${`url(` + imageBackground + `)`}`, backgroundSize: 'cover'}
        : {}

    const dropdownLinks1 = [
        dropdownLinkLabel1 &&
            dropdownLinkUrl1 && {label: dropdownLinkLabel1, url: dropdownLinkUrl1},
        dropdownLinkLabel2 &&
            dropdownLinkUrl2 && {label: dropdownLinkLabel2, url: dropdownLinkUrl2},
        dropdownLinkLabel3 &&
            dropdownLinkUrl3 && {label: dropdownLinkLabel3, url: dropdownLinkUrl3},
        dropdownLinkLabel4 &&
            dropdownLinkUrl4 && {label: dropdownLinkLabel4, url: dropdownLinkUrl4},
        dropdownLinkLabel5 &&
            dropdownLinkUrl5 && {label: dropdownLinkLabel5, url: dropdownLinkUrl5},
        dropdownLinkLabel6 && dropdownLinkUrl6 && {label: dropdownLinkLabel6, url: dropdownLinkUrl6}
    ].filter(Boolean)

    const StoryTellingRef = useRef(null)
    const pdTagManager = usePdTagManager()
    const appOrigin = useAppOrigin()

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                pdTagManager.viewComponent(typeId, name)
            }
        })

        if (StoryTellingRef.current) observer.observe(StoryTellingRef.current)
        return () => observer.disconnect()
    }, [])

    const handleGTMClickEvent = () => {
        pdTagManager.selectComponent({
            componentId: typeId,
            componentName: name,
            titleName: title,
            linkUrl:
                buttonLink1?.indexOf('.com') !== -1 ? buttonLink1 : `${appOrigin}${buttonLink1}`,
            videoUrl: desktopVideo || mobileVideo,
            imageUrl: desktopVideo ? undefined : desktopVideo || mobileImage
        })
    }

    return desktopImage || mobileImage || shouldDisplayVideo ? (
        <Stack
            position="relative"
            direction={{base: 'column', md: 'row'}}
            gap="0"
            background={imageBackgroundColor}
            ref={StoryTellingRef}
        >
            {!shouldDisplayVideo && (desktopImage || mobileImage) ? (
                <Box
                    className="media-type"
                    data-type="image"
                    position="relative"
                    flex="0 0 50%"
                    order={bannerComponentOrder}
                >
                    <Banner
                        desktopImage={desktopImage}
                        mobileImage={mobileImage}
                        imageAlt={imageAlt}
                        onClick={handleGTMClickEvent}
                    />

                    {overlayLink && (
                        <Link
                            href={overlayLink}
                            aria-label={overlayLinkAriaLabel}
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            height="100%"
                            onClick={handleGTMClickEvent}
                        />
                    )}
                </Box>
            ) : null}
            {shouldDisplayVideo && (
                <Box
                    className="media-type"
                    data-type="video"
                    position="relative"
                    flex="0 0 50%"
                    order={bannerComponentOrder}
                >
                    <Video
                        desktopVideo={desktopVideo}
                        mobileVideo={mobileVideo || desktopVideo}
                        desktopVideoThumbnail={desktopVideoThumbnail}
                        mobileVideoThumbnail={mobileVideoThumbnail}
                        videoDisableAutoPlay={videoDisableAutoPlay}
                        videoEnableSound={videoEnableSound}
                        videoAlt={videoAlt}
                        onButtonClick={handleGTMClickEvent}
                    />
                </Box>
            )}
            <Stack
                order={headLineComponentOrder}
                flex="0 0 50%"
                gap={{base: '20px', lg: '15px'}}
                paddingX={{base: '20px', md: '30px', lg: '80px', xl: '100px', '2xl': '200px'}}
                paddingY={{base: '30px', md: '40px'}}
                justifyContent="center"
                textAlign={headlineAlignment}
                alignItems={headlineAlignment}
                {...imageStyles}
            >
                {(imageLogo1 || imageLogo2) && (
                    <Box
                        display="inline-flex"
                        margin="0 auto"
                        marginBottom={{base: '10px', lg: '25px'}}
                    >
                        {imageLogo1 && (
                            <Image
                                alt={imageLogo1Alt}
                                height={{base: '45px', lg: '100px'}}
                                width={{base: '45px', lg: '100px'}}
                                htmlHeight={isDesktop ? '100px' : '45px'}
                                htmlWidth={isDesktop ? '100px' : '45px'}
                                src={imageLogo1}
                            />
                        )}
                        {imageLogo1 && imageLogo2 && (
                            <Divider
                                orientation="vertical"
                                height={{base: '45px', lg: '100px'}}
                                borderColor="black.50"
                                marginLeft="30px"
                                marginRight="30px"
                            />
                        )}
                        {imageLogo2 && (
                            <Image
                                alt={imageLogo2Alt}
                                height={{base: '45px', lg: '100px'}}
                                width={{base: '45px', lg: '100px'}}
                                htmlHeight={isDesktop ? '100px' : '45px'}
                                htmlWidth={isDesktop ? '100px' : '45px'}
                                src={imageLogo2}
                            />
                        )}
                    </Box>
                )}
                {subCopy && (
                    <Text
                        as={subCopyTag}
                        color={subCopyColor}
                        fontFamily={subCopyFontFamily || 'heading'}
                        fontSize="24px"
                        fontWeight="300"
                        lineHeight="26px"
                        letterSpacing="0.09px"
                    >
                        {subCopy}
                    </Text>
                )}

                {title && (
                    <Text
                        as={titleTag}
                        color={titleColor}
                        fontFamily={titleFontFamily || 'caslon'}
                        fontSize={{base: '28px', lg: '42px'}}
                        fontWeight="400"
                        lineHeight="normal"
                        letterSpacing={{base: '0.18px', xl: '0.21px'}}
                    >
                        {title}
                    </Text>
                )}

                {bodyCopy && (
                    <Box
                        color={bodyCopyColor}
                        fontWeight="300"
                        fontFamily={bodyFontFamily || 'inter'}
                        fontSize={{base: '17px', xl: '24px'}}
                        letterSpacing="0.09px"
                        sx={{
                            p: {
                                fontWeight: '300',
                                fontFamily: bodyFontFamily || 'inter',
                                fontSize: {base: '17px', lg: '24px'},
                                lineHeight: {base: '27px', lg: '36px'},
                                letterSpacing: '0.09px'
                            }
                        }}
                        dangerouslySetInnerHTML={{__html: bodyCopy}}
                    />
                )}

                {hasButton1 && (
                    <HeroButton
                        text={buttonText1}
                        url={buttonLink1}
                        color={buttonColor1} // Text color fallback
                        bgColor={buttonBgColor1} // Legacy fallback
                        buttonBgColor={buttonBgColor1 ? buttonBgColor1 : ''}
                        buttonTextColor={buttonColor1 ? buttonColor1 : ''}
                        ctaSizeMobile={ctaSizeMobile}
                        ctaSizeDesktop={ctaSizeDesktop}
                        ctaTypeMobile={ctaTypeMobile}
                        ctaTypeDesktop={ctaTypeDesktop}
                        letterSpacing="0.085px"
                        display="flex"
                        justifyContent={headlineAlignment}
                        dropdownLinks={dropdownLinks1}
                        marginTop={{base: '10px', md: '25px'}}
                        marginBottom="5px"
                        aria-label={buttonLinkAriaLabel1 || buttonText1}
                        onButtonClick={handleGTMClickEvent}
                    />
                )}
            </Stack>
            {calloutsComponents.map((component, index) => (
                <PDComponent key={index} component={component} />
            ))}
        </Stack>
    ) : null
}

StoryTelling.propTypes = {
    desktopImage: PropTypes.string,
    mobileImage: PropTypes.string,
    imageAlt: PropTypes.string,
    desktopImagePosition: PropTypes.string,
    mobileImagePosition: PropTypes.string,
    imageBackgroundColor: PropTypes.string,
    imageBackground: PropTypes.string,

    title: PropTypes.string,
    titleTag: PropTypes.string,
    titleFontFamily: PropTypes.string,
    titleColor: PropTypes.string,
    headlineAlignment: PropTypes.string,

    subCopy: PropTypes.string,
    subCopyFontFamily: PropTypes.string,
    subCopyColor: PropTypes.string,
    subCopyTag: PropTypes.string,

    bodyCopy: PropTypes.string,
    bodyCopyColor: PropTypes.string,
    bodyFontFamily: PropTypes.string,

    buttonText1: PropTypes.string,
    buttonColor1: PropTypes.string,
    buttonBgColor1: PropTypes.string,
    buttonLink1: PropTypes.string,
    buttonLinkAriaLabel1: PropTypes.string,
    dropdownLinkLabel1: PropTypes.string,
    dropdownLinkUrl1: PropTypes.string,
    dropdownLinkLabel2: PropTypes.string,
    dropdownLinkUrl2: PropTypes.string,
    dropdownLinkLabel3: PropTypes.string,
    dropdownLinkUrl3: PropTypes.string,
    dropdownLinkLabel4: PropTypes.string,
    dropdownLinkUrl4: PropTypes.string,
    dropdownLinkLabel5: PropTypes.string,
    dropdownLinkUrl5: PropTypes.string,
    dropdownLinkLabel6: PropTypes.string,
    dropdownLinkUrl6: PropTypes.string,

    ctaTypeMobile: PropTypes.string,
    ctaTypeDesktop: PropTypes.string,
    ctaSizeMobile: PropTypes.string,
    ctaSizeDesktop: PropTypes.string,

    overlayLink: PropTypes.string,
    overlayLinkAriaLabel: PropTypes.string,

    imageLogo1: PropTypes.string,
    imageLogo2: PropTypes.string,
    imageLogo1Alt: PropTypes.string,
    imageLogo2Alt: PropTypes.string,

    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string,
                    typeId: PropTypes.string,
                    data: PropTypes.object
                })
            )
        })
    ),
    desktopVideo: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideo: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    videoAlt: PropTypes.string,
    videoEnableSound: PropTypes.string,
    videoDisableAutoPlay: PropTypes.string,

    name: PropTypes.any,
    typeId: PropTypes.string
}

export default StoryTelling
